import { useState } from "react";
import { useMenu } from "../hooks/useMenu";
import {
  Upload,
  Search,
  Filter,
  Grid,
  List,
  Edit,
  Trash2,
  Package,
} from "lucide-react";
import Header from "./Header";
import AddItemForm from "./AddItemForm";
import EditItemForm from "./EditItemForm";
import Table from "./Table";
import Toast from "./Toast";
import ConfirmDialog from "./ConfirmDialog";
import AlertDialog from "./AlertDialog";

const Menu = () => {
  const {
    showAddForm,
    showEditForm,
    editingItem,
    items,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    loading,
    hasMore,
    totalItems,
    toast,
    showToast,
    closeToast,
    confirmDialog,
    alertDialog,
    closeAlert,
    handleAddItem,
    handleShowAddForm,
    handleCloseAddForm,
    handleEditItem,
    handleUpdateItem,
    handleCloseEditForm,
    handleDeleteItem,
    reloadItems,
  } = useMenu();

  // Get unique categories and brands for filters
  const categories = Array.from(
    new Set(items.map((item) => item.category).filter(Boolean))
  );
  const brands = Array.from(
    new Set(items.map((item) => item.brand).filter(Boolean))
  );

  const [filterCategory, setFilterCategory] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'grid' - default to table

  // Filter items based on selected filters
  const filteredItems = items
    .filter((item) =>
      filterCategory ? item.category === filterCategory : true
    )
    .filter((item) => (filterBrand ? item.brand === filterBrand : true));

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}/products/upload-excel`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Bulk upload failed");
      
      showToast("Bulk items uploaded successfully!", "success");
      e.target.value = "";
      reloadItems();
    } catch (err) {
      showToast(err.message || "Bulk upload failed", "error");
    }
  };

  const clearFilters = () => {
    setFilterCategory("");
    setFilterBrand("");
    setSearchTerm("");
    setPage(1); // Reset to first page when clearing filters
  };

  // Reset to page 1 when category or brand filter changes
  const handleCategoryChange = (value) => {
    setFilterCategory(value);
    setPage(1);
  };

  const handleBrandChange = (value) => {
    setFilterBrand(value);
    setPage(1);
  };

  const activeFiltersCount = [filterCategory, filterBrand, searchTerm].filter(
    Boolean
  ).length;

  return (
    <div className="products-page">
      <Header
        title="Products"
        subtitle="Manage your product catalog"
        onRefresh={reloadItems}
        refreshing={loading}
        onAddNew={handleShowAddForm}
        addNewLabel="Add Product"
        showFilter={true}
        onFilterToggle={() => setShowFilters(!showFilters)}
        filterActive={showFilters}
        actions={[
          {
            label: "Bulk Upload",
            icon: <Upload size={16} />,
            onClick: () => document.getElementById("csv-upload-input").click(),
            variant: "btn-secondary",
            mobileHidden: true,
          },
        ]}
      />

      <div className="products-content">
        {/* Hidden file input for bulk upload */}
        <input
          id="csv-upload-input"
          type="file"
          accept=".xlsx, .csv, .xls, .xlsm , .xlsb"
          style={{ display: "none" }}
          onChange={handleBulkUpload}
        />

        {/* Filters Section */}
        {showFilters && (
          <div className="filters-section">
            <div className="filters-content">
              <div className="filters-header">
                <h6 className="filters-title">
                  <Filter size={16} />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="filters-count">
                      ({activeFiltersCount})
                    </span>
                  )}
                </h6>
                {activeFiltersCount > 0 && (
                  <button className="btn-clear-filters" onClick={clearFilters}>
                    Clear All
                  </button>
                )}
              </div>

              <div className="filters-grid">
                <div className="filter-group">
                  <label className="filter-label">Search Products</label>
                  <div className="search-input-container">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search by name, brand, category..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Category</label>
                  <select
                    className="filter-select"
                    value={filterCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Brand</label>
                  <select
                    className="filter-select"
                    value={filterBrand}
                    onChange={(e) => handleBrandChange(e.target.value)}
                  >
                    <option value="">All Brands</option>
                    {brands.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Controls */}
        <div className="view-controls">
          <div className="view-actions">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table view"
              >
                <List size={16} />
              </button>
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid view"
              >
                <Grid size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Products Content */}
        <div className="products-display">
          {viewMode === "table" ? (
            <Table
              columns={{
                title: "PRODUCTS",
                headers: [
                  {
                    label: "S.No",
                    key: "sno",
                    className: "border-0 ps-3",
                    cellClassName: "ps-3 fw-bold text-secondary",
                    cellStyle: { minWidth: 60 },
                  },
                  {
                    label: "Preview",
                    key: "preview",
                    className: "border-0",
                    cellClassName: "ps-2",
                    cellStyle: { minWidth: 80 },
                  },
                  {
                    label: "Product Name",
                    key: "name",
                    className: "border-0 ps-2",
                    cellClassName: "ps-2 fw-bold",
                  },
                  {
                    label: "Brand",
                    key: "brand",
                    className: "border-0 ps-2",
                    cellClassName: "ps-2",
                  },
                  {
                    label: "Category",
                    key: "category",
                    className: "border-0 ps-2",
                    cellClassName: "ps-2",
                  },
                  {
                    label: "MRP Price",
                    key: "price",
                    className: "border-0 text-center",
                    cellClassName: "fw-bold text-center",
                    cellStyle: { minWidth: 100 },
                  },
                  {
                    label: "Selling Price",
                    key: "discountPrice",
                    className: "border-0 text-center",
                    cellClassName: "fw-bold text-success text-center",
                    cellStyle: { minWidth: 100 },
                  },
                  {
                    label: "Actions",
                    key: "actions",
                    className: "border-0 text-center",
                    cellClassName: "text-center",
                    cellStyle: { minWidth: 160 },
                  },
                ],
              }}
              data={filteredItems.map((item, idx) => ({
                ...item,
                sno: idx + 1,
                preview: item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: 50,
                      height: 50,
                      objectFit: "cover",
                      borderRadius: 6,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      background: "var(--gray-100)",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Package size={20} className="text-muted" />
                  </div>
                ),
                actions: (
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => handleEditItem(item.id)}
                      style={{ minWidth: "65px" }}
                    >
                      <Edit size={14} className="me-1" />
                      Edit
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDeleteItem(item.id)}
                      style={{ minWidth: "75px" }}
                    >
                      <Trash2 size={14} className="me-1" />
                      Delete
                    </button>
                  </div>
                ),
              }))}
              emptyIcon={<Package size={64} className="text-muted mb-3" />}
              emptyTitle="No items found"
              emptyMessage="Try adjusting your search or add a new item"
            />
          ) : (
            /* Grid View */
            <>
              {filteredItems.length === 0 ? (
                <div className="empty-state">
                  <Package size={64} className="text-muted mb-3" />
                  <h5 className="text-muted">No items found</h5>
                  <p className="text-muted">
                    Try adjusting your search or add a new item
                  </p>
                </div>
              ) : (
                <div className="row g-3">
                  {filteredItems.map((item, idx) => (
                    <div
                      key={item.id}
                      className="col-xxl-2 col-xl-3 col-lg-4 col-md-6 col-sm-6"
                    >
                      <div className="card product-card h-100">
                        <div className="product-image-container">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="product-image"
                            />
                          ) : (
                            <div className="product-image-placeholder">
                              <Package size={48} className="text-muted" />
                            </div>
                          )}
                        </div>
                        <div className="card-body">
                          <h6
                            className="card-title text-truncate"
                            title={item.name}
                          >
                            {item.name}
                          </h6>
                          <div className="product-meta">
                            <small className="text-muted">{item.brand}</small>
                            <span className="mx-2">•</span>
                            <small className="text-muted">
                              {item.category}
                            </small>
                          </div>
                          <div className="product-pricing mt-2">
                            <div className="current-price">
                              ₹{item.discountPrice || item.price}
                            </div>
                            {item.discountPrice &&
                              item.price !== item.discountPrice && (
                                <div className="original-price">
                                  ₹{item.price}
                                </div>
                              )}
                          </div>
                        </div>
                        <div className="card-footer bg-transparent border-top-0">
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-outline-secondary btn-sm flex-fill"
                              onClick={() => handleEditItem(item.id)}
                            >
                              <Edit size={14} className="me-1" />
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm flex-fill"
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              <Trash2 size={14} className="me-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Pagination - Only show when no local filters are applied */}
          {!loading && totalItems > 0 && !filterCategory && !filterBrand && (
            <div className="d-flex justify-content-center align-items-center gap-3 py-3">
              <button
                className="btn btn-primary btn-sm"
                disabled={loading || page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className="small text-muted">
                Page {page} • {totalItems} items
              </span>
              <button
                className="btn btn-primary btn-sm"
                disabled={loading || !hasMore}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
          
          {/* Show info when filters are applied */}
          {(filterCategory || filterBrand) && (
            <div className="d-flex justify-content-center py-3">
              <small className="text-muted">
                Showing {filteredItems.length} filtered result{filteredItems.length !== 1 ? 's' : ''} from page {page}
              </small>
            </div>
          )}
        </div>
      </div>

      {showAddForm && (
        <AddItemForm 
          onClose={handleCloseAddForm} 
          onAddItem={handleAddItem}
          onShowToast={showToast}
        />
      )}

      {showEditForm && editingItem && (
        <EditItemForm
          item={editingItem}
          onClose={handleCloseEditForm}
          onUpdateItem={handleUpdateItem}
          onShowToast={showToast}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}

      {/* Alert Dialog */}
      {alertDialog && (
        <AlertDialog
          title={alertDialog.title}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={closeAlert}
        />
      )}

      <style jsx>{`
        .products-page {
          min-height: 100vh;
          background-color: var(--gray-50);
        }

        .products-content {
          padding: 0 1.5rem 2rem;
        }

        /* Filters Section */
        .filters-section {
          margin-bottom: 1.5rem;
        }

        .filters-content {
          background: white;
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          box-shadow: var(--shadow-sm);
          padding: 1.5rem;
        }

        .filters-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
        }

        .filters-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700);
        }

        .filters-count {
          background: var(--primary-100);
          color: var(--primary-600);
          padding: 0.125rem 0.5rem;
          border-radius: var(--radius-md);
          font-size: 0.75rem;
          font-weight: 500;
        }

        .btn-clear-filters {
          background: none;
          border: none;
          color: var(--primary-600);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius-sm);
          transition: all var(--transition-fast);
        }

        .btn-clear-filters:hover {
          background-color: var(--primary-50);
        }

        .filters-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 1.5rem;
          align-items: end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .filter-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--gray-700);
        }

        .search-input-container {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400);
        }

        .search-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: 0.875rem;
          background: white;
          transition: all var(--transition-fast);
        }

        .search-input:focus {
          outline: none;
          border-color: var(--primary-500);
          box-shadow: 0 0 0 3px var(--primary-100);
        }

        .filter-select {
          padding: 0.75rem;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: 0.875rem;
          background: white;
          transition: all var(--transition-fast);
        }

        .filter-select:focus {
          outline: none;
          border-color: var(--primary-500);
          box-shadow: 0 0 0 3px var(--primary-100);
        }

        /* View Controls */
        .view-controls {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-bottom: 0.75rem;
          padding: 0.5rem 0;
        }

        .view-actions {
          display: flex;
          align-items: center;
        }

        .view-toggle {
          display: flex;
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-md);
          overflow: hidden;
          background: white;
        }

        .view-btn {
          padding: 0.5rem 0.75rem;
          border: none;
          background: white;
          color: var(--gray-600);
          cursor: pointer;
          transition: all var(--transition-fast);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .view-btn:hover {
          background: var(--gray-50);
          color: var(--gray-800);
        }

        .view-btn.active {
          background: var(--primary-600);
          color: white;
        }

        .view-btn + .view-btn {
          border-left: 1px solid var(--gray-300);
        }

        .view-btn.active + .view-btn {
          border-left-color: var(--primary-600);
        }

        /* Products Display */
        .products-display {
          background: white;
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          box-shadow: var(--shadow-sm);
          overflow: hidden;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .products-content {
            padding: 0 1rem 2rem;
          }

          .filters-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .view-controls {
            justify-content: center;
            padding: 0.25rem 0;
          }

          .filters-content {
            padding: 1rem;
          }

          .filters-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
        }

        @media (max-width: 575px) {
          .products-content {
            padding: 0 0.5rem 1rem;
          }

          .filters-content {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Menu;
